// ── Os dois seletores de estilo, em UM lugar ────────────────────────────────
//
// As miniaturas animadas nasceram na tela de CRIACAO (VideoEditorView). Agora o
// estilo tambem e trocavel no editor manual, e uma lista de nomes ("karaoke,
// simples, serifada, classica") faz a pessoa escolher as cegas — a miniatura
// existe justamente para ela VER o que vai acontecer com o video. Duplicar o
// desenho nos dois lugares era garantir que um dia eles divergissem, entao ele
// mora aqui e as duas telas importam.
//
// O `tema` existe porque as duas telas tem fundos opostos: a criacao e clara
// (slate-50) e o editor e escuro (slate-950). O DESENHO da miniatura e o mesmo
// nos dois — o que muda e a moldura.
//
// Os numeros das animacoes vem do render:
//  - karaoke: cada palavra entra em 230ms (7 quadros a 30fps) com ease-out
//    cubico e sobe 34px num quadro de 1080 de largura. Escalado para a miniatura
//    o salto daria ~5px, invisivel: uso 10px e assumo o desvio, porque a
//    miniatura existe para a pessoa ver o movimento, nao para medi-lo.
//  - passo de 260ms entre palavras, 3 por cue (maxPalavras do render).
//  - headline: entrada de 270ms subindo 24px (~8px na miniatura), bloco inteiro.
// A troca das cues estaticas NAO tem transicao: os stops em 49,9%/50% sao um
// pulo, nao uma fusao — a ausencia de animacao e a informacao que separa esses
// tres estilos do karaoke, e para ver ausencia a pessoa precisa ver a troca.
import type { CSSProperties } from 'react';
import type { VideoEditorCaptionStyle, VideoEditorHeadlineStyle } from '../../types/videoEditor';

// Familias do render (SIMPLE_VARIANTS em SimpleCaptions.tsx): Poppins 600
// estreitada, Libre Baskerville 700, Inter 500. O app nao carrega essas faces, e
// baixar webfont so para a miniatura nao se paga — a pilha cai no que existe,
// mantendo o CONTRASTE que e a informacao (sem serifa pesada x serifa x sem
// serifa leve).
const CAP_FONT_SANS = "'Poppins','Plus Jakarta Sans',system-ui,sans-serif";
const CAP_FONT_SERIF = "'Libre Baskerville',Georgia,serif";
const CAP_FONT_BODY = "'Inter',system-ui,sans-serif";

// Amostras curtas de proposito: a miniatura tem ~150px de largura e a frase do
// render ("E assim que sua legenda ira aparecer") viraria borrao nesse tamanho.
const CAP_CUE_A = ['você', 'precisa', 'ver'];
const CAP_CUE_B = ['antes', 'de', 'postar'];
// 'classica' e o unico com duas linhas (maxPalavras 14 contra 3 dos outros).
const CAP_LONG_A = ['você precisa ver isso', 'antes de postar hoje'];
const CAP_LONG_B = ['e é isso que muda', 'o resultado do vídeo'];
// Sempre duas linhas, como HEADLINE_STYLES no render.
const HL_LINES = ['o seu gancho', 'fica assim na tela'];

// As dicas cabem em UMA linha no card estreito do passo a passo (grid-cols-2 num
// modal de 448px: sobram ~178px de texto, ~35 caracteres a 10px). Nao e capricho
// de redacao: numa grade de 2x2 a altura da FILA e a do card mais alto, entao uma
// dica de duas linhas engordava a fila inteira em 13px. Medido na etapa 2 em
// 1366x768: as duas grades somavam 450px e a etapa pedia 768px de conteudo contra
// 689px visiveis — 79px de transbordo. Encurtar 'karaoke' e 'simples' (e 'realce'
// e 'misto' na grade de gancho) tirou 26px desses 79 sem tocar na miniatura, que e
// a razao de o seletor existir. O que se perdeu foi adjetivo — "peso maximo" e
// "de frase" estao DESENHADOS na miniatura logo acima da dica.
export const CAPTION_STYLE_CARDS: Array<{ value: VideoEditorCaptionStyle; label: string; hint: string }> = [
  { value: 'karaoke', label: 'Karaokê', hint: 'Palavra a palavra, subindo' },
  { value: 'simples', label: 'Simples', hint: 'Troca seca, sem animação' },
  { value: 'serifada', label: 'Serifada', hint: 'Troca seca, tipo com serifa' },
  { value: 'classica', label: 'Clássica', hint: 'Duas linhas, corpo menor' },
];

// ── Catalogo de overlay, em UM lugar ────────────────────────────────────────
//
// Este catalogo vivia DUPLICADO: uma copia em VideoEditorView.tsx (~:34) e outra
// em ViralResearchView.tsx (~:668). Elas nao "iam divergir um dia" — elas JA
// tinham divergido em quatro pontos quando foram reunidas aqui, e o dano nao foi
// teorico: a correcao do `flex flex-col` no <button> (o desalinhamento de ~7px
// entre os rotulos, medido) foi aplicada SO na copia do wizard. O editor ficou
// com os rotulos desalinhados por nao ser o arquivo que alguem estava editando.
//
// As divergencias e para que lado cada uma foi reconciliada:
//  - dica do 'none': "So o corte" (editor) x "So o video" (wizard). Nenhuma das
//    duas serve para os dois: o editor entrega um CORTE, o wizard entrega cenas
//    montadas. A dica agora fala do que e igual nos dois — nao ter texto.
//  - `disabled:opacity-50` x `-40`: ficou 50, o valor do editor (e o unico que
//    aparece em outro lugar do app).
//  - grade `grid-cols-2 sm:grid-cols-4` x `grid-cols-3`: virou o parametro
//    `colunas`, que e como CaptionStyleCards ja resolvia o mesmo problema.
//  - o paragrafo de apoio do 'caption' divergia EM SUBSTANCIA (ver
//    OverlayStyleHelp).
export type OverlayStyle = 'none' | 'hook' | 'caption' | 'question_box';

// 'none' e um card e nao uma caixa de selecao a parte. Antes existiam os dois: os
// cards escolhiam QUAL overlay e uma caixa "Overlays da brand" la embaixo ligava
// e desligava tudo. Dois controles para a mesma decisao, em lugares diferentes da
// tela, e o texto da caixa descrevia so o estilo 'hook' — quem escolhia legenda e
// lia "tipografia cinetica e hook de retencao" recebia uma promessa errada.
// Pior: a caixa so era desenhada quando existia marca e o envio fazia
// `addOverlays && !!brand`, entao um job SEM marca saia sem legenda nenhuma,
// anulando no front o cuidado que o backend ja tinha de nao exigir marca no modo
// legenda. `exigeBrand` resolve isso pelo lado certo: so o 'hook' e tipografia da
// marca; legenda vem do Whisper e a caixinha e tarja preta com cartao branco.
export const OVERLAY_STYLE_CARDS: Array<{ value: OverlayStyle; label: string; hint: string; exigeBrand?: boolean }> = [
  { value: 'hook', label: 'Hook + destaques', hint: 'Bloco tipográfico da marca ao longo do vídeo', exigeBrand: true },
  { value: 'caption', label: 'Apenas legenda', hint: 'Gancho curto + legenda acompanhando a fala' },
  { value: 'question_box', label: 'Caixinha', hint: 'Pergunta na tela do começo ao fim' },
  // Dica reconciliada: as duas copias diziam o que SAI ("so o corte" no editor,
  // "so o video" no wizard) e cada uma so valia para a sua tela. O que as duas
  // telas tem em comum e a AUSENCIA de texto — e e isso que o card promete.
  { value: 'none', label: 'Sem overlay', hint: 'Nenhum texto desenhado na tela' },
];

// Mesma regra de uma linha das dicas de legenda (ver o comentario de
// CAPTION_STYLE_CARDS): 'realce' e 'misto' quebravam em duas linhas e levavam a
// fila de baixo da grade de gancho de 102px para 115px.
export const HEADLINE_STYLE_CARDS: Array<{ value: VideoEditorHeadlineStyle; label: string; hint: string }> = [
  { value: 'outline', label: 'Contorno', hint: 'Branco com contorno, sem caixa' },
  { value: 'card', label: 'Cartão', hint: 'Caixa alta sobre cartão escuro' },
  { value: 'realce', label: 'Realce', hint: 'Um bloco por linha, cor da marca' },
  { value: 'misto', label: 'Misto', hint: 'Linha leve + pesada na cor da marca' },
];

export const CAPTION_DEMO_CSS = `
/* KARAOKE — laco de 1,3s, nao de 2,4s. As duracoes ABSOLUTAS de cada palavra sao
   as do render (233ms de entrada, ~260ms entre elas) — mexer nelas seria mentir
   sobre a velocidade. O que encurtou foi a ESPERA depois da ultima palavra:
   medido, com 2,4s o movimento acabava em 751ms e o card ficava congelado 69% do
   tempo, e quem da uma olhada rapida pegava o quadro parado. Com 1,3s o movimento
   ocupa 58% do laco: 233/1300 = 17,9% de entrada, 260/1300 = 20% de espacamento.

   O 1,3s vale SO para vev-kar-*. A troca seca dos estaticos (vev-cut-*, aplicada
   em CaptionCutDemo) continua em 1,8s de proposito: ali nao ha movimento para
   "ocupar" o laco, ha DUAS cues alternando a 50% cada. Encurtar para 1,3s daria
   0,65s por cue e o card leria como pisca-pisca em vez de troca de frase — os
   dois lacos sao diferentes porque medem coisas diferentes. */
@keyframes vev-kar-1 { 0% { opacity: 0; transform: translateY(10px) } 17.9%, 100% { opacity: 1; transform: translateY(0) } }
@keyframes vev-kar-2 { 0%, 20% { opacity: 0; transform: translateY(10px) } 37.9%, 100% { opacity: 1; transform: translateY(0) } }
@keyframes vev-kar-3 { 0%, 40% { opacity: 0; transform: translateY(10px) } 57.9%, 100% { opacity: 1; transform: translateY(0) } }
@keyframes vev-cut-a { 0%, 49.9% { opacity: 1 } 50%, 100% { opacity: 0 } }
@keyframes vev-cut-b { 0%, 49.9% { opacity: 0 } 50%, 100% { opacity: 1 } }
.vev-cut-b { opacity: 0 }
@media (prefers-reduced-motion: reduce) {
  /* Congelar no estado final era PERDER A INFORMACAO. Medido no componente real
     com "reduce" ligado: 0 animacoes e os QUATRO cards estaticos e identicos entre
     si — ninguem consegue distinguir karaoke de simples, que e a unica coisa que o
     card existe para mostrar. E no Windows 11 basta desligar os efeitos de
     animacao em Acessibilidade para cair aqui, entao nao e um canto raro.
     A resposta certa e tirar o MOVIMENTO, nao a informacao: o karaoke vira um
     rastro estatico (a palavra da vez opaca, as seguintes esmaecidas e um pouco
     mais baixas, que e exatamente o que a animacao mostra em um instante) e os
     estaticos mostram UMA cue, sem troca. */
  .vev-anim { animation: none !important }
  /* A propriedade transition tambem, nao so animation. Este bloco zerava apenas
     as animacoes, e o passo a passo de overlay usa transition-all nas bolinhas de
     etapa e transition em TODOS os cards (a moldura de selecao). Com "reduce"
     ligado o movimento sumia dos thumbs mas as molduras e as bolinhas
     continuavam deslizando — meia promessa cumprida e justamente a parte que a
     pessoa clica. */
  .vev-anim, .vev-sem-transicao, .vev-sem-transicao * { transition: none !important }
  .vev-cut-b { display: none !important }
  .vev-kar-2 { opacity: 0.45; transform: translateY(3px) }
  .vev-kar-3 { opacity: 0.18; transform: translateY(6px) }
}
`;

// `atraso` desencontra os lacos: sem ele os tres estilos estaticos trocavam de
// frase todos no MESMO instante, e quatro miniaturas piscando juntas leem como
// falha da tela em vez de tres demonstracoes independentes.
function CaptionCutDemo({ cueA, cueB, tipo, atraso }: { cueA: string[]; cueB: string[]; tipo: CSSProperties; atraso: number }) {
  const cues = [
    { key: 'a', lines: cueA, cls: 'vev-cut-a' },
    { key: 'b', lines: cueB, cls: 'vev-cut-b' },
  ];
  return (
    <div className="absolute inset-x-1 bottom-[22%]">
      {cues.map(cue => (
        <p
          key={cue.key}
          className={`vev-anim ${cue.cls} absolute inset-x-0 bottom-0 text-center leading-[1.18] text-[#f4f1e9] [text-shadow:0_1px_3px_rgba(0,0,0,.75)]`}
          style={{ ...tipo, animation: `${cue.cls} 1.8s linear infinite`, animationDelay: `-${atraso}s` }}
        >
          {cue.lines.map((linha, i) => (
            <span key={i} className="block">{linha}</span>
          ))}
        </p>
      ))}
    </div>
  );
}

// Miniatura dos quatro estilos de gancho. Sempre duas linhas, como no render.
//
// SEM ANIMACAO, de proposito. A miniatura animava a entrada (270ms subindo 24px,
// copiada da referencia), e o render TAMBEM animava — mas isso era um defeito:
// o primeiro quadro do video e a CAPA, e com entrada animada a capa saia sem o
// gancho. O render passou a desenhar o bloco estatico desde o quadro zero (ver
// headlineFrames no motor de overlays), entao uma miniatura que anima estaria
// prometendo um movimento que o MP4 nao tem — que e exatamente o tipo de
// divergencia entre tela e arquivo que este seletor existe para eliminar.
//
// O `atraso` deixou de ter uso aqui e fica na assinatura so para o chamador nao
// mudar; o desencontro de lacos que ele resolvia era das legendas.
function HeadlineDemo({ value, accent }: { value: VideoEditorHeadlineStyle; accent: string; atraso?: number }) {
  const anim: CSSProperties = {
    fontFamily: CAP_FONT_SANS,
  };
  if (value === 'realce') {
    // Um bloco solido POR LINHA (nao um retangulo atras das duas): e o desenho do
    // render, e no tamanho da miniatura a folga entre os blocos e o que mostra isso.
    return (
      <div className="flex flex-col items-center gap-[2px]" style={anim}>
        {HL_LINES.map(linha => (
          <span
            key={linha}
            className="rounded-[2px] px-[3px] pb-[1px] text-[9px] font-black leading-[1.04] text-white"
            style={{ background: accent }}
          >
            {linha}
          </span>
        ))}
      </div>
    );
  }
  if (value === 'card') {
    return (
      <div
        className="rounded-[3px] bg-[#232326] px-[5px] py-[3px] text-center text-[8px] font-black uppercase leading-[1.06] text-white"
        style={anim}
      >
        {HL_LINES.map(linha => (
          <span key={linha} className="block">{linha}</span>
        ))}
      </div>
    );
  }
  if (value === 'misto') {
    return (
      <div className="text-center leading-[0.98]" style={anim}>
        <span className="block text-[9px] font-light text-white">{HL_LINES[0]}</span>
        <span className="block text-[10px] font-black" style={{ color: accent }}>{HL_LINES[1]}</span>
      </div>
    );
  }
  // 'outline': contorno preto grosso (7px em 1080 -> 2px aqui), sem caixa nenhuma.
  return (
    <div
      className="text-center text-[9px] font-extrabold leading-[1.02] text-white"
      style={{ ...anim, WebkitTextStroke: '2px #000', paintOrder: 'stroke fill' }}
    >
      {HL_LINES.map(linha => (
        <span key={linha} className="block">{linha}</span>
      ))}
    </div>
  );
}

type Tema = 'claro' | 'escuro';

const MOLDURA: Record<Tema, { ativo: string; inativo: string; rotuloAtivo: string; rotulo: string; dica: string; etiqueta: string }> = {
  claro: {
    ativo: 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-200',
    inativo: 'border-slate-200 bg-white hover:border-slate-300',
    rotuloAtivo: 'text-indigo-700',
    rotulo: 'text-slate-700',
    dica: 'text-slate-500',
    etiqueta: 'text-slate-500',
  },
  escuro: {
    ativo: 'border-indigo-400 bg-indigo-500/15 ring-1 ring-indigo-400/40',
    inativo: 'border-white/10 bg-white/[0.03] hover:bg-white/[0.07]',
    rotuloAtivo: 'text-indigo-200',
    rotulo: 'text-slate-300',
    dica: 'text-slate-500',
    etiqueta: 'text-slate-500',
  },
};

// Miniatura de cada estilo de legenda. O `flex flex-col` no <button> nao e
// decoracao: <button> centraliza o proprio conteudo na VERTICAL, entao o card com
// a dica mais longa definia a altura da linha e os outros desciam ~7px — os
// rotulos saiam desalinhados entre si. Medido por boundingBox depois: os thumbs
// alinhados no mesmo y.
export function CaptionStyleCards({
  value,
  onChange,
  tema = 'claro',
  colunas = 'grid-cols-2 sm:grid-cols-4',
  altura = 'h-16',
}: {
  value: VideoEditorCaptionStyle;
  onChange: (estilo: VideoEditorCaptionStyle) => void;
  tema?: Tema;
  colunas?: string;
  // `altura` do palco da miniatura. O padrao h-16 e o da tela de criacao, que tem
  // a pagina inteira para ela. O passo a passo de producao pede h-12: com h-16 a
  // etapa 2 media 807px de conteudo e em 1366x768 so 689px cabiam — o rodape com
  // "Voltar" e "Continuar" caia fora da tela (medido em 781-821 num modal que
  // acabava em 730). Nao e uma escolha de gosto, e o que faz a etapa caber.
  altura?: string;
}) {
  const t = MOLDURA[tema];
  return (
    <div className={`vev-sem-transicao grid gap-2 ${colunas}`}>
      {CAPTION_STYLE_CARDS.map(card => {
        const ativo = value === card.value;
        return (
          <button
            key={card.value}
            type="button"
            onClick={() => onChange(card.value)}
            aria-pressed={ativo}
            className={`flex flex-col rounded-xl border p-2 text-left transition ${ativo ? t.ativo : t.inativo}`}
          >
            <div className={`relative mb-1.5 ${altura} w-full overflow-hidden rounded-lg bg-gradient-to-b from-slate-600 to-slate-800`}>
              {/* Karaoke: uma linha so, tres palavras (maxPalavras do render),
                  Poppins peso maximo, branca. Cada palavra entra em 230ms
                  subindo, com 260ms entre elas, e a cue e trocada sem saida —
                  por isso o laco recomeca seco. */}
              {card.value === 'karaoke' && (
                <div className="absolute inset-x-1 bottom-[22%] flex items-baseline justify-center gap-[3px] whitespace-nowrap">
                  {CAP_CUE_A.map((palavra, i) => (
                    <span
                      key={palavra}
                      className={`vev-anim vev-kar-${i + 1} text-[10px] font-black leading-none text-white [text-shadow:0_1px_2px_rgba(0,0,0,.75)]`}
                      style={{
                        fontFamily: CAP_FONT_SANS,
                        letterSpacing: '-0.02em',
                        animation: `vev-kar-${i + 1} 1.3s cubic-bezier(.33,1,.68,1) infinite`,
                      }}
                    >
                      {palavra}
                    </span>
                  ))}
                </div>
              )}
              {card.value === 'simples' && (
                // escala 0.9 na horizontal: e o escalaX de CAPTION_STYLES
                <CaptionCutDemo
                  cueA={[CAP_CUE_A.join(' ')]}
                  cueB={[CAP_CUE_B.join(' ')]}
                  tipo={{ fontFamily: CAP_FONT_SANS, fontWeight: 600, fontSize: '9px', transform: 'scaleX(0.9)' }}
                  atraso={0.3}
                />
              )}
              {card.value === 'serifada' && (
                <CaptionCutDemo
                  cueA={[CAP_CUE_A.join(' ')]}
                  cueB={[CAP_CUE_B.join(' ')]}
                  tipo={{ fontFamily: CAP_FONT_SERIF, fontWeight: 700, fontSize: '9px' }}
                  atraso={0.9}
                />
              )}
              {card.value === 'classica' && (
                // 42px contra 66px dos outros no render: na miniatura o corpo
                // menor e as DUAS linhas sao a informacao.
                <CaptionCutDemo
                  cueA={CAP_LONG_A}
                  cueB={CAP_LONG_B}
                  tipo={{ fontFamily: CAP_FONT_BODY, fontWeight: 500, fontSize: '7px' }}
                  atraso={1.4}
                />
              )}
            </div>
            <p className={`text-[11px] font-bold leading-tight ${ativo ? t.rotuloAtivo : t.rotulo}`}>{card.label}</p>
            <p className={`mt-0.5 text-[10px] leading-snug ${t.dica}`}>{card.hint}</p>
          </button>
        );
      })}
    </div>
  );
}

export function HeadlineStyleCards({
  value,
  onChange,
  accent,
  tema = 'claro',
  colunas = 'grid-cols-2 sm:grid-cols-4',
  altura = 'h-14',
}: {
  value: VideoEditorHeadlineStyle;
  onChange: (estilo: VideoEditorHeadlineStyle) => void;
  accent: string;
  tema?: Tema;
  colunas?: string;
  // Mesmo motivo de `altura` em CaptionStyleCards: o passo a passo passa h-12
  // para a etapa 2 caber em 1366x768 sem empurrar o rodape fora da tela.
  altura?: string;
}) {
  const t = MOLDURA[tema];
  return (
    <div className={`vev-sem-transicao grid gap-2 ${colunas}`}>
      {HEADLINE_STYLE_CARDS.map((card, i) => {
        const ativo = value === card.value;
        return (
          <button
            key={card.value}
            type="button"
            onClick={() => onChange(card.value)}
            aria-pressed={ativo}
            className={`flex flex-col rounded-xl border p-2 text-left transition ${ativo ? t.ativo : t.inativo}`}
          >
            <div className={`relative mb-1.5 ${altura} w-full overflow-hidden rounded-lg bg-gradient-to-b from-slate-600 to-slate-800`}>
              <div className="absolute inset-0 flex items-center justify-center px-1">
                <HeadlineDemo value={card.value} accent={accent} atraso={i * 0.45} />
              </div>
            </div>
            <p className={`text-[11px] font-bold leading-tight ${ativo ? t.rotuloAtivo : t.rotulo}`}>{card.label}</p>
            <p className={`mt-0.5 text-[10px] leading-snug ${t.dica}`}>{card.hint}</p>
          </button>
        );
      })}
    </div>
  );
}

// A miniatura desenha o LAYOUT que vai sair (bloco no meio, legenda baixa e
// pequena, caixinha em tarja + cartao no ALTO, quadro limpo), com o mesmo
// posicionamento do render. E o que separa "escolher olhando" de "escolher no
// escuro e descobrir depois de pagar o credito".
//
// Os quatro desenhos eram identicos byte a byte nas duas copias — o unico ramo
// que existia so no editor era o 'question_box', e ele veio inteiro.
export function OverlayStyleThumb({ value }: { value: OverlayStyle }) {
  return (
    <div className="relative mb-1.5 aspect-[9/16] w-full overflow-hidden rounded-lg bg-gradient-to-b from-slate-500 to-slate-700">
      {value === 'hook' && (
        <div className="absolute inset-x-1 top-1/2 -translate-y-1/2 text-center leading-[1.05]">
          <span className="block text-[9px] font-black text-white [text-shadow:0_1px_0_rgba(0,0,0,.8)]">QUAL</span>
          <span className="block text-[11px] font-black text-white [text-shadow:0_1px_0_rgba(0,0,0,.8)]">IMAGEM</span>
          <span className="block text-[7px] font-bold text-white/90">VOCÊ</span>
          <span className="block text-[10px] font-black italic text-rose-300 [text-shadow:0_1px_0_rgba(0,0,0,.8)]">compra?</span>
        </div>
      )}
      {value === 'caption' && (
        <>
          <div className="absolute inset-x-1 top-[22%] text-center leading-[1.05]">
            <span className="block text-[8px] font-black text-white [text-shadow:0_1px_0_rgba(0,0,0,.8)]">QUAL IMAGEM</span>
          </div>
          <div className="absolute inset-x-1 top-[78%] -translate-y-1/2 text-center">
            <span className="text-[7px] font-black text-white [text-shadow:0_1px_0_rgba(0,0,0,.9)]">você prefere </span>
            <span className="text-[7px] font-black text-white/40">essa</span>
          </div>
        </>
      )}
      {value === 'question_box' && (
        // Medido no render: a caixinha ocupa y 0,07 a 0,18 e x 0,22 a 0,78.
        // A miniatura tem de mostrar isso — ela fica no TOPO, nao no meio.
        <div className="absolute left-[22%] right-[22%] top-[7%] overflow-hidden rounded">
          <div className="bg-[#1C1C1E] px-1 py-0.5 text-center text-[5px] font-semibold text-white">
            O que você me perguntaria?
          </div>
          <div className="bg-white px-1 py-1 text-center text-[6px] text-[#1C1C1E]">Sua pergunta</div>
        </div>
      )}
      {/* 'none' mostra o quadro limpo — e literalmente o que sai. */}
      {value === 'none' && (
        <div className="absolute inset-0 flex items-center justify-center">
          <span aria-hidden="true" className="text-[16px] font-light text-white/35">—</span>
        </div>
      )}
    </div>
  );
}

// Paragrafo de apoio de cada estilo. Estava duplicado e as duas versoes do
// 'caption' divergiam EM SUBSTANCIA — nao em redacao:
//
//  - o editor dizia "legenda simples acompanhando a fala — palavra a palavra,
//    branca, embaixo, sem bloco tipografico". "Palavra a palavra" e FALSO para
//    tres dos quatro estilos que a etapa seguinte oferece: so o 'karaoke' entra
//    palavra por palavra; 'simples', 'serifada' e 'classica' trocam a cue INTEIRA
//    de uma vez (buildCaptionGroups agrupa e as animacoes vev-cut-* deste arquivo
//    sao exatamente a troca seca). Prometer palavra a palavra e depois oferecer
//    tres estilos que nao fazem isso e a mesma promessa errada que a caixa
//    "Overlays da brand" fazia.
//  - o wizard dizia "os tempos vem da transcricao do proprio audio, entao este
//    modo nunca escreve o que nao foi falado" — verdadeiro — mas omitia o resto.
//
// Terceira correcao, esta de CONTRADICAO e nao de duplicacao: o paragrafo dizia
// "sem bloco tipografico" e a etapa 2 que essa MESMA escolha abre oferece os
// ganchos 'Cartao' ("Caixa alta sobre cartao escuro") e 'Realce' ("Um bloco por
// linha, cor da marca") — que sao blocos, literalmente. O texto queria dizer "sem
// o bloco tipografico DA MARCA", que e o desenho do modo 'hook' (comparar com a
// dica do card 'hook': "Bloco tipografico da marca ao longo do video" — os dois
// usam agora o mesmo termo, com o "da marca" que era justamente o que faltava).
// Prometer ausencia de bloco e depois oferecer dois estilos de bloco e a mesma
// classe de erro do "palavra a palavra" logo acima.
//
// O "nao depende do agente de overlays" do editor foi CONFERIDO nos dois
// backends e e verdade nos dois: com overlayStyle='caption' o caminho e
// buildCaptionManifest (deterministico, sem LLM) e nao buildOverlayManifest
// (marketingStudioOverlayService.enhanceAvatarVideo ~:5049 e videoEditorService
// ~:8824, captionMode). Entao ele fica; o que saiu foi o "palavra a palavra".
export function OverlayStyleHelp({ value, className = '' }: { value: OverlayStyle; className?: string }) {
  if (value === 'hook') {
    return (
      <p className={className}>
        A IA escreve um gancho para os primeiros segundos e distribui overlays de destaque ao longo do vídeo,
        na tipografia e nas cores da marca.
      </p>
    );
  }
  if (value === 'caption') {
    return (
      <p className={className}>
        Gancho curto na abertura e, depois dele, <strong>legenda acompanhando a fala</strong>, sem o bloco
        tipográfico da marca. Os tempos vêm da transcrição do próprio áudio, então este modo não passa pelo
        agente de overlays: sai mais rápido e nunca escreve o que não foi falado.
      </p>
    );
  }
  if (value === 'none') {
    return (
      <p className={className}>
        O vídeo sai limpo: nenhum texto é desenhado na tela. Útil quando a legenda vai ser posta na própria
        plataforma — ou quando as cenas já trazem texto.
      </p>
    );
  }
  // 'question_box': o apoio nao e so texto, e texto + os dois campos digitados.
  // Quem oferece a caixinha desenha o proprio bloco (hoje so o editor de video).
  return null;
}

// Grade de cards de overlay. `filtrar` existe para o wizard de producao esconder
// a 'question_box' SEM tocar no catalogo: la o contrato de envio leva apenas
// { overlayStyle, captionStyle, headlineStyle } e a caixinha depende de dois
// textos digitados — oferece-la seria entregar o card que promete e nao cumpre.
export function OverlayStyleCards({
  value,
  onChange,
  brand,
  tema = 'claro',
  colunas = 'grid-cols-2 sm:grid-cols-4',
  filtrar,
}: {
  value: OverlayStyle;
  onChange: (estilo: OverlayStyle) => void;
  // So interessa se EXISTE marca; o tipo fica solto para as duas telas passarem
  // o objeto que elas ja tem sem este arquivo precisar conhecer MarketingBrand.
  brand: unknown;
  tema?: Tema;
  colunas?: string;
  filtrar?: (estilo: OverlayStyle) => boolean;
}) {
  const t = MOLDURA[tema];
  const cards = filtrar ? OVERLAY_STYLE_CARDS.filter(c => filtrar(c.value)) : OVERLAY_STYLE_CARDS;
  return (
    <div className={`vev-sem-transicao grid gap-2 ${colunas}`}>
      {cards.map(card => {
        // 'hook' e tipografia DA MARCA: sem marca cadastrada ele nao tem de onde
        // tirar fonte e cor. Legenda vem da transcricao e o quadro limpo nao
        // depende de nada, entao esses dois nunca bloqueiam.
        const bloqueado = !!card.exigeBrand && !brand;
        const ativo = value === card.value && !bloqueado;
        const motivo = 'Precisa de uma marca cadastrada: este estilo usa a tipografia dela.';
        return (
          <button
            key={card.value}
            type="button"
            // `aria-disabled` e NAO `disabled`: com o atributo real o card saia da
            // ordem de tabulacao e o motivo do bloqueio (que so existia num
            // `title`, disparado por mouse) nunca chegava a quem navega por
            // teclado ou usa leitor de tela. Medido antes: tabbable=false e a
            // string do motivo ausente do texto da tela. Agora o card e
            // alcancavel, se anuncia desabilitado, o clique nao muda nada e o
            // motivo esta ESCRITO embaixo do rotulo.
            aria-disabled={bloqueado || undefined}
            onClick={() => { if (!bloqueado) onChange(card.value); }}
            aria-pressed={ativo}
            title={bloqueado ? motivo : undefined}
            // `flex flex-col` nao e decoracao: <button> centraliza o proprio
            // conteudo na vertical, entao o card com a dica mais longa definia a
            // altura da linha e os outros desciam ~7px — os rotulos saiam
            // desalinhados entre si. Esta correcao existia SO na copia do wizard;
            // o editor rodou desalinhado ate esta extracao.
            className={`flex flex-col rounded-xl border p-2 text-left transition ${bloqueado ? 'cursor-not-allowed opacity-50' : ''} ${ativo ? t.ativo : t.inativo}`}
          >
            <OverlayStyleThumb value={card.value} />
            <p className={`text-[11px] font-bold leading-tight ${ativo ? t.rotuloAtivo : t.rotulo}`}>{card.label}</p>
            <p className={`mt-0.5 text-[10px] leading-snug ${t.dica}`}>{card.hint}</p>
            {bloqueado && (
              <p className="mt-1 text-[10px] font-semibold leading-snug text-amber-500">Precisa de uma marca cadastrada.</p>
            )}
          </button>
        );
      })}
    </div>
  );
}
